/* eslint-disable react/prop-types */
import React, { useEffect } from 'react';
import Grid from '@material-ui/core/Grid';
import { Field } from 'formik';
import { CheckboxWithLabel, TextField } from 'formik-material-ui';
import { makeStyles, Typography, Box } from '@material-ui/core';
import SelectMenuItem from '../../core/SelectMenuItem';
import FormikDatePickerField from '../../core/formik/FormikDatePickerField';
import FormItem from '../../core/FormItem';
import Fieldset from '../../core/Fieldset';
import { staticDataHelper } from '../../../config';
import FormikAddressAutoSuggestField from '../../address/FormikAddressAutoSuggestField';
import FormikAlphaNumericInputField from '../../core/formik/FormikAlphaNumericInputField';
import FormikButtonSelectField from '../../core/formik/FormikButtonSelectField';
import startupConfig from '../../../config/startupConfig';
import { STATES } from '../../../constants';
import { PASSPORT_TOOLTIP } from './constants';
import CardNumberActSvg from '../../svg/CardNumberActSvg';
import CardNumberNswSvg from '../../svg/CardNumberNswSvg';
import CardNumberNtSvg from '../../svg/CardNumberNtSvg';
import CardNumberQldSvg from '../../svg/CardNumberQldSvg';
import CardNumberSaSvg from '../../svg/CardNumberSaSvg';
import CardNumberTasSvg from '../../svg/CardNumberTasSvg';
import CardNumberVicSvg from '../../svg/CardNumberVicSvg';
import CardNumberWaSvg from '../../svg/CardNumberWaSvg';
import IdVerificationErrorAlert from './IdVerificationErrorAlert';
import { scrollWindowToTop } from '../../../utils/regoUtils';

// eslint-disable-next-line no-unused-vars
const useStyles = makeStyles((theme) => ({
  idVerificationForm: {
    color: theme.palette.common.midnightBlue,
  },
  subTitles: {
    padding: theme.spacing(0),
  },
  nameFieldsGuide: {
    color: '#828D99',
  },
  mainTitleLabel: {
    paddingBottom: '0 !important',
  },
  rows: {
    margin: theme.spacing(1, 0),
    '& .MuiTypography-subtitle1': {
      fontSize: 14,
      fontWeight: 500,
    },
  },
  driversLicenceState: {
    '& .MuiMenuItem-root': {
      fontSize: 8,
    },
    '& .MuiFormLabel-root.Mui-focused': {
      color: 'inherit',
    },
  },
  stateInput: {
    backgroundColor: theme.palette.primary.contrastText,
    height: theme.spacing(5),
    fontSize: 14,
    '& .MuiSelect-root': {
      backgroundColor: theme.palette.primary.contrastText,
      paddingTop: theme.spacing(1.5),
      paddingBottom: theme.spacing(1.5),
    },
  },
  stateInputLabel: {
    color: theme.palette.grey['400'],
    transform: 'translate(10px, 10px) scale(1)',
  },
  checkboxLabel: {
    fontSize: 14,
  },
  cardNumberGuideHeader: {
    fontSize: 14,
    fontWeight: 500,
    margin: 0,
    padding: 0,
  },
  cardNumberGuideText: {
    fontSize: 12,
    marginTop: theme.spacing(1),
  },
  cardNumberGuideBox: {
    '& .MuiSvgIcon-root': {
      fill: 'none',
    },
    width: theme.spacing(43),
    backgroundColor: '#F2FBFC',
    borderRadius: '6px',
  },
}));

const IdVerificationFieldset = ({
  values,
  isSubmitting,
  errorMessage,
  isNameListVisible,
  isTrustWithIndividualTrustee,
  trusteeNamesList,
  directorList,
  setFieldValue,
  setFieldError,
  resetForm,
  isMyNameNotHere,
  myNameIsNotHere,
  isFormInputAndSubmitVisible,
  maximumDateofBirth,
  handleNameListSelectionChange,
  delay,
  earliestAcceptablePassportExpiry,
  handleDocumentTypeSelectionChange,
  earliestAcceptableDriverLicenceExpiry,
  idVerificationResult,
  isVerifyIdPrefilledError,
}) => {
  const classes = useStyles();
  const isDocTypeDriverLicence = staticDataHelper.isDocumentTypeDriversLicence(values.documentType);
  const isDocTypePassport = staticDataHelper.isDocumentTypePassport(values.documentType);
  const isSsoFlowEnabled = startupConfig.get().featureConfiguration.isSsoFlowEnabled;
  const stateToSvgCardComponent = {
    [STATES.ACT]: CardNumberActSvg,
    [STATES.NSW]: CardNumberNswSvg,
    [STATES.NT]: CardNumberNtSvg,
    [STATES.QLD]: CardNumberQldSvg,
    [STATES.SA]: CardNumberSaSvg,
    [STATES.TAS]: CardNumberTasSvg,
    [STATES.VIC]: CardNumberVicSvg,
    [STATES.WA]: CardNumberWaSvg,
  };

  const getSelectLabel = (value) => {
    if (!value) {
      return (
        <Box className={classes.selectLabelBox}>
          <Typography className={classes.selectLabelText}>Select</Typography>
        </Box>
      );
    }
    return null;
  };

  const getCardNumberGuide = (state) => {
    const SvgCardComponent = stateToSvgCardComponent[state];

    if (SvgCardComponent) {
      return (
        <Box className={classes.cardNumberGuideBox}>
          <SvgCardComponent data-testid={`uia-cardNumber${state}Svg`} />
        </Box>
      );
    }

    return (
      <Box className={classes.cardNumberGuideBox}>
        <Typography className={classes.cardNumberGuideHeader}>Finding your card number</Typography>
        <Typography className={classes.cardNumberGuideText}>
          Selecting “State of issue’ (above) will show you where to find the card number on your
          driver licence.
        </Typography>
      </Box>
    );
  };

  useEffect(() => {
    if (errorMessage && errorMessage.length > 0) {
      scrollWindowToTop();
    }
  }, [errorMessage]);

  return (
    <Fieldset disabled={isSubmitting} className={classes.idVerificationForm}>
      <Grid container spacing={2}>
        <IdVerificationErrorAlert
          errorMessage={errorMessage}
          isVerifyIdPrefilledError={isVerifyIdPrefilledError}
          idVerificationResult={idVerificationResult}
        />
        {isNameListVisible && (
          <>
            <Grid item xs={12} className={classes.mainTitleLabel}>
              <Typography variant="body2" component="label">
                Select your name from the list below:
              </Typography>
            </Grid>
            <FormItem>
              <Field
                name="selectedName"
                options={isTrustWithIndividualTrustee ? trusteeNamesList : directorList}
                component={FormikButtonSelectField}
                onSelectionChange={handleNameListSelectionChange(
                  setFieldValue,
                  resetForm,
                  isSsoFlowEnabled,
                )}
                value={isMyNameNotHere ? myNameIsNotHere.id : values.selectedName}
                data-testid="selectedName"
              />
            </FormItem>
          </>
        )}
        {isFormInputAndSubmitVisible && (
          <React.Fragment>
            <Grid item xs={12}>
              <Grid container spacing={2} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h2" className={classes.subTitles}>
                Identity details
              </Typography>
            </Grid>
            <Grid item xs={12} className={classes.mainTitleLabel}>
              <Typography variant="body2" component="label">
                Accepted documents include Australian driver licence or Australian passport
              </Typography>
            </Grid>
            <FormItem xs={12} data-testid="documentType">
              <Field
                name="documentType"
                options={staticDataHelper.documentTypes}
                component={FormikButtonSelectField}
                value={values.documentType}
                onSelectionChange={handleDocumentTypeSelectionChange(setFieldValue)}
              />
            </FormItem>
            {values.documentType && (
              <Grid item xs={12}>
                <Grid container>
                  {isDocTypeDriverLicence && (
                    <>
                      <Grid item xs={12} className={classes.rows}>
                        <FormItem
                          label="State of issue"
                          xs={12}
                          sm={2}
                          className={classes.driversLicenceState}
                        >
                          <Field
                            type="text"
                            autoFocus
                            id="driversLicenceState"
                            name="driversLicenceState"
                            fullWidth
                            select
                            component={TextField}
                            InputProps={{
                              className: classes.stateInput,
                              onChange: (event) => {
                                if (event && event.target.value) {
                                  setFieldValue('driversLicenceState', event.target.value, false);
                                }
                              },
                              onBlur: () => {
                                setFieldError('driversLicenceState', '');
                              },
                            }}
                            InputLabelProps={{
                              className: classes.stateInputLabel,
                              shrink: false,
                            }}
                            label={getSelectLabel(values.driversLicenceState, classes)}
                            variant="outlined"
                            data-testid="driversLicenceState"
                            SelectProps={{
                              MenuProps: {
                                anchorOrigin: {
                                  vertical: 'bottom',
                                  horizontal: 'left',
                                },
                                transformOrigin: {
                                  vertical: 'top',
                                  horizontal: 'left',
                                },
                                getContentAnchorEl: null,
                                PaperProps: {
                                  style: {
                                    boxShadow:
                                      '0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)',
                                  },
                                },
                              },
                            }}
                          >
                            {Object.keys(STATES).map((option) => (
                              <SelectMenuItem
                                data-testid={`uia-selectState-${option}`}
                                key={`selectState-${option}`}
                                value={option}
                              >
                                {option}
                              </SelectMenuItem>
                            ))}
                          </Field>
                        </FormItem>
                      </Grid>
                      <Grid item xs={12} className={classes.rows}>
                        <Grid item xs={12} sm={4}>
                          <FormItem label="Licence number">
                            <Field
                              type="text"
                              name="driversLicenseNumber"
                              component={FormikAlphaNumericInputField}
                              variant="outlined"
                              fullWidth
                              value={values.driversLicenseNumber}
                              inputProps={{
                                maxLength: 10,
                              }}
                              data-testid="driversLicenseNumber"
                            />
                          </FormItem>
                        </Grid>
                      </Grid>
                    </>
                  )}

                  {isDocTypePassport && (
                    <Grid item xs={12} className={classes.rows}>
                      <Grid item xs={12} sm={4}>
                        <FormItem label="Australian passport number">
                          <Field
                            type="text"
                            name="passportNumber"
                            component={FormikAlphaNumericInputField}
                            variant="outlined"
                            fullWidth
                            value={values.passportNumber}
                            inputProps={{
                              maxLength: 9,
                            }}
                            autoFocus
                            data-testid="passportNumber"
                          />
                        </FormItem>
                      </Grid>
                    </Grid>
                  )}
                  {isDocTypeDriverLicence && (
                    <>
                      <Grid item xs={12} className={classes.rows}>
                        <Grid item xs={12} sm={4}>
                          <FormItem label="Card number">
                            <Field
                              name="driversLicenceCardNumber"
                              component={FormikAlphaNumericInputField}
                              inputProps={{
                                maxLength: 10,
                              }}
                              fullWidth
                              variant="outlined"
                              value={values.driversLicenceCardNumber}
                              data-testid="uia-driversLicenceCardNumber"
                            />
                          </FormItem>
                        </Grid>
                      </Grid>
                      <Grid item xs={12} className={classes.rows}>
                        {getCardNumberGuide(values.driversLicenceState)}
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12} className={classes.rows}>
                    <Grid item xs={12} sm={4}>
                      <FormItem
                        label="Expiry date"
                        tooltip={isDocTypePassport ? PASSPORT_TOOLTIP : undefined}
                      >
                        <Field
                          name="documentExpirationDate"
                          component={FormikDatePickerField}
                          variant="outlined"
                          fullWidth
                          value={values.documentExpirationDate}
                          data-testid="documentExpirationDate"
                          minDate={
                            isDocTypeDriverLicence
                              ? earliestAcceptableDriverLicenceExpiry
                              : earliestAcceptablePassportExpiry
                          }
                        />
                      </FormItem>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} className={classes.rows}>
                    <Grid item xs={12} sm={6}>
                      <FormItem
                        label={isDocTypeDriverLicence ? 'First name' : 'Given names'}
                        xs={12}
                      >
                        <Field
                          type="text"
                          name="firstName"
                          component={FormikAlphaNumericInputField}
                          variant="outlined"
                          fullWidth
                          value={values.firstName}
                          inputProps={{
                            maxLength: 40,
                          }}
                          data-testid="firstName"
                          placeholder={isDocTypeDriverLicence ? 'without middle name' : ''}
                          useSpecialCharacters
                        />
                        <Typography variant="subtitle2" className={classes.nameFieldsGuide}>
                          as shown on your {isDocTypeDriverLicence ? 'driver licence' : 'passport'}
                        </Typography>
                      </FormItem>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} className={classes.rows}>
                    <Grid item xs={12} sm={6}>
                      <FormItem label="Last name" xs={12}>
                        <Field
                          type="text"
                          name="lastName"
                          component={FormikAlphaNumericInputField}
                          variant="outlined"
                          fullWidth
                          value={values.lastName}
                          inputProps={{
                            maxLength: 80,
                          }}
                          data-testid="lastName"
                          placeholder={isDocTypeDriverLicence ? 'without middle name' : ''}
                          useSpecialCharacters
                        />
                        <Typography variant="subtitle2" className={classes.nameFieldsGuide}>
                          as shown on your {isDocTypeDriverLicence ? 'driver licence' : 'passport'}
                        </Typography>
                      </FormItem>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} className={classes.rows}>
                    <Grid item xs={12} sm={4}>
                      <FormItem label="Date of birth" xs={12}>
                        <Field
                          name="birthDate"
                          component={FormikDatePickerField}
                          variant="outlined"
                          fullWidth
                          value={values.birthDate}
                          maxDate={maximumDateofBirth}
                          disableFuture
                          data-testid="birthDate"
                        />
                      </FormItem>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            )}
            <Grid item xs={12}>
              <Typography variant="h2">Contact details</Typography>
            </Grid>
            <Grid item xs={12} className={classes.rows}>
              <Grid item xs={12} sm={6}>
                <FormItem label="Email address" xs={12}>
                  {/*
                              IMPORTANT
                              =========
                              Intentionally not using type='email' here because of the materia ui space issue with email fields.
                              We rely on the validation schema to validate the value instead.
                              */}
                  <Field
                    name="email"
                    component={TextField}
                    variant="outlined"
                    fullWidth
                    value={values.email}
                    data-testid="email"
                    onBlur={() => setFieldError('email', '')}
                  />
                </FormItem>
              </Grid>
            </Grid>
            <Grid item xs={12} className={classes.rows}>
              <FormItem xs={12} sm={12} label="Residential address">
                <Field
                  type="text"
                  name="homeAddress"
                  component={FormikAddressAutoSuggestField}
                  variant="outlined"
                  fullWidth
                  value={values.homeAddress}
                  onSuggestionSelected={(suggestion) => {
                    setFieldValue('homeAddress', suggestion, true);
                  }}
                  delay={delay}
                  data-testid="homeAddress"
                />
              </FormItem>
            </Grid>
            <FormItem xs={12}>
              <Field
                name="consent"
                component={CheckboxWithLabel}
                Label={{
                  // eslint-disable-next-line max-len
                  label: (
                    <div className={classes.checkboxLabel}>
                      I agree to have my identity information verified with the issuer or official
                      record holder and I agree to{' '}
                      <a
                        href="https://www.shift.com.au/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Shift&apos;s collection notice
                      </a>
                    </div>
                  ),
                }}
                color="primary"
                data-testid="consent"
              />
            </FormItem>
          </React.Fragment>
        )}
      </Grid>
    </Fieldset>
  );
};

export default IdVerificationFieldset;
